import { useState, useCallback, useEffect } from 'react';

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
}

interface UseAuthReturn {
  user: User | null;
  token: string | null;
  login: () => Promise<void>;
  logout: () => void;
  setAuth: (token: string, user: User) => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Module-level flag to prevent multiple simultaneous auth checks
let globalAuthCheckInProgress = false;
let globalAuthCheckPromise: Promise<User | null> | null = null;

/**
 * SECURITY NOTE: Token Storage in HTTP-only Cookies
 * 
 * ✅ SECURE: JWT tokens are now stored in HTTP-only cookies set by the backend.
 * HTTP-only cookies are not accessible via JavaScript, preventing XSS token theft.
 * 
 * Implementation (Story 2.2):
 * - Backend sets JWT tokens in HTTP-only cookies (not accessible via JavaScript)
 * - Frontend uses credentials: 'include' in fetch requests to send cookies
 * - Token is automatically included in requests, no manual handling needed
 * - User data is stored in component state only (not localStorage)
 * 
 * References:
 * - OWASP JWT Security Cheat Sheet: https://cheatsheetseries.owasp.org/cheatsheets/JSON_Web_Token_for_Java_Cheat_Sheet.html
 * - OWASP XSS Prevention: https://cheatsheetseries.owasp.org/cheatsheets/Cross_Site_Scripting_Prevention_Cheat_Sheet.html
 */

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount (shared across all hook instances)
  useEffect(() => {
    // Skip if we're on the OAuth callback page (it will handle auth itself)
    if (window.location.pathname === '/auth/oauth/callback') {
      setLoading(false);
      return;
    }

    // Skip if we're on the login page (no need to check auth)
    if (window.location.pathname === '/auth/login') {
      setLoading(false);
      return;
    }

    // If we already have a user, don't check again
    if (user) {
      setLoading(false);
      return;
    }

    // If there's already a check in progress, wait for it
    if (globalAuthCheckInProgress && globalAuthCheckPromise) {
      globalAuthCheckPromise.then((userData) => {
        if (userData) {
          setUser(userData);
          setToken('cookie-based-token');
        } else {
          setUser(null);
          setToken(null);
        }
        setLoading(false);
      }).catch(() => {
        setUser(null);
        setToken(null);
        setLoading(false);
      });
      return;
    }

    // Start new shared auth check
    globalAuthCheckInProgress = true;
    globalAuthCheckPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/users/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          // Update all hook instances that are waiting
          setUser(userData);
          setToken('cookie-based-token');
          return userData;
        } else if (response.status === 401) {
          // Token is invalid or expired - clear auth state
          setUser(null);
          setToken(null);
          // Note: We don't call logout() here to avoid redirect loops
          // The ProtectedRoute will handle redirecting to login
          return null;
        } else {
          // Other error - clear auth state
          setUser(null);
          setToken(null);
          return null;
        }
      } catch (error) {
        setUser(null);
        setToken(null);
        return null;
      } finally {
        globalAuthCheckInProgress = false;
        setLoading(false);
      }
    })();
  }, [user]);

  const login = useCallback(async () => {
    setLoading(true);
    try {
      // Redirect to backend OAuth endpoint
      const oauthUrl = `${API_URL}/auth/google`;
      window.location.href = oauthUrl;
      // Note: We don't set loading to false here because we're redirecting
      // The loading state will be reset when the component unmounts
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const setAuth = useCallback((newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    // Mark as checked so we don't re-check
    globalAuthCheckInProgress = false;
    globalAuthCheckPromise = null;
    // Token is stored in HTTP-only cookie by backend, no localStorage needed
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint to clear HTTP-only cookie
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      // Log error but continue with local state cleanup
      console.error('Logout error:', error);
    } finally {
      // Always clear local state, even if backend call fails
      setUser(null);
      setToken(null);
      // Reset global auth check state to prevent re-authentication
      globalAuthCheckInProgress = false;
      globalAuthCheckPromise = null;
    }
  }, []);

  const isAuthenticated = !!user && !!token;

  return {
    user,
    token,
    login,
    logout,
    setAuth,
    isAuthenticated,
    loading,
  };
};

